import React, { useEffect, useState } from "react";
import StaffInfoForm from "./StaffInfoForm";
import EmploymentInfoForm from "./EmploymentInfoForm";
import PaymentInfoForm from "./PaymentInfoForm";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { User } from "@/models/user.model";
import { CreateStaffInfo, StaffInfo } from "@/models/staff-info.model";
import { CircleDollarSign } from "lucide-react";

import {
  BriefcaseBusiness,
  ChevronLeft,
  ChevronRight,
  UserRound,
} from "lucide-react";
import {
  CreateEmploymentInfo,
  EmploymentInfo,
} from "@/models/employment-info.model";
import { PaymentInfo } from "@/models/payment-info.model";
import { Button } from "@/components/ui/button";
import OnboardTimeline from "./OnboardTimeline";
import Card from "@/components/Card";
import { onboardUser } from "@/api/users";
import { toast } from "@/hooks/use-toast";
import LeaveAllowanceForm from "./LeaveAllowanceForm";
import {
  CreateLeaveBalanceOnboard,
  LeaveBalance,
} from "@/models/leave-balances.model";
import OnboardNavigationGuard from "./OnboardNavigationGuard";
export interface PageMetadata {
  number: number;
  title: string;
  description: string;
  icon: JSX.Element;
  iconTitle: string;
}

interface Pages {
  staffInfo: PageMetadata;
  employmentInfo: PageMetadata;
  paymentInfo: PageMetadata;
  leaveAllowance: PageMetadata;
}

export const OnboardPages: Pages = {
  staffInfo: {
    number: 1,
    title: "Staff Information",
    description: "Fill in your employee's personal details",
    icon: <UserRound size={24} />,
    iconTitle: "Staff",
  },
  employmentInfo: {
    number: 2,
    title: "Employment Information",
    description: "Fill in your employee's employment details",
    icon: <BriefcaseBusiness size={24} />,
    iconTitle: "Employment",
  },
  paymentInfo: {
    number: 3,
    title: "Payment Information",
    description: "Fill in your employee's payment details",
    icon: <CircleDollarSign size={24} />,
    iconTitle: "Payment",
  },
  leaveAllowance: {
    number: 4,
    title: "Leave Allowance",
    description: "Fill in your employee's leave allowance details in days",
    icon: <CircleDollarSign size={24} />,
    iconTitle: "Leave",
  },
};

function OnboardPage() {
  const navigate = useNavigate();

  let { id } = useParams();

  const userId = Number(id);
  const [isOnboardingComplete, setIsOnboardingComplete] = useState(false);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [page, setPage] = useState<number>(OnboardPages.staffInfo.number);
  const [staffInfo, setStaffInfo] = useState<CreateStaffInfo | null>(null);
  const [employmentInfo, setEmploymentInfo] =
    useState<CreateEmploymentInfo | null>(null);
  const [paymentInfo, setPaymentInfo] = useState<PaymentInfo | null>(null);
  const [totalDaysValues, setTotalDaysValues] = useState<
    Record<number, number> // leaveTypeId: totalDays
  >({});
  const [submit, setSubmit] = useState<boolean>(false);

  // Get current page metadata
  const getCurrentPageMetadata = (): PageMetadata => {
    const currentPage = Object.values(OnboardPages).find(
      (pageData) => pageData.number === page
    );
    return currentPage || OnboardPages.staffInfo;
  };

  const currentPage = getCurrentPageMetadata();
  useEffect(() => {
    if (!userId) {
      toast({
        title: "Invalid access",
        description: "No user data found. Please try registering again.",
        variant: "destructive",
      });
      // navigate("/admin/users");
      return;
    }
  }, [userId, navigate]);

  useEffect(() => {
    const handleSubmit = async () => {
      if (!submit) {
        return;
      }

      if (!userId) {
        toast({
          title: "Error",
          description: "No user data found. Please try again.",
          variant: "destructive",
        });
        return;
      }

      if (
        submit &&
        staffInfo &&
        employmentInfo &&
        paymentInfo &&
        totalDaysValues
      ) {
        try {
          setIsSubmitting(true);
          console.log("leave balances", totalDaysValues);
          const leaveBalances: CreateLeaveBalanceOnboard[] = Object.entries(
            totalDaysValues
          ).map(([leaveTypeId, totalDays]) => ({
            leaveTypeId: Number(leaveTypeId),
            totalDays,
          }));
          const res = await onboardUser(userId, {
            paymentInfo,
            employmentInfo,
            staffInfo,
            leaveBalances,
          });
          if (res) {
            toast({
              title: "User onboarded successfully",
              description: "User has been successfully onboarded",
            }); // Redirect to users page

            console.log("User onboarded", res);
            navigate("/admin/users");
          } else {
            toast({
              title: "An error occurred",
              description: "Please try again",
              variant: "destructive",
            });
          }
        } catch (error) {
          console.log(error);
          toast({
            title: "An error occurred",
            description: "Please try again",
            variant: "destructive",
          });
        } finally {
          setIsSubmitting(false);
        }
      }
    };
    handleSubmit();
  }, [submit]);

  useEffect(() => {
    console.log(staffInfo, employmentInfo, paymentInfo);
  }, [staffInfo, employmentInfo, paymentInfo]);


  useEffect(() => {
    console.log("onboard user", userId);
  }, [userId]);

  return (
    <>
      <OnboardNavigationGuard isOnboardingComplete={isOnboardingComplete} userId={userId} />


      <div className="flex w-full flex-col h-[100vh] p-4">
        <Card styles="flex w-full flex-col p-5 h-full">
          <div className="flex w-full justify-center p-4">
            <OnboardTimeline currentPage={currentPage} />
          </div>
          <div className="flex flex-col gap-2 p-4 self-center">
            <h1 className="text-2xl font-semibold text-center">
              {currentPage.title}
            </h1>
            <p className="text-gray-500 text-center">{currentPage.description}</p>
          </div>

          <div className="h-full">
            {page === OnboardPages.staffInfo.number && (
              <StaffInfoForm
                setStaffInfo={setStaffInfo}
                staffInfo={staffInfo}
                setPage={setPage}
              />
            )}
            {page === OnboardPages.employmentInfo.number && (
              <EmploymentInfoForm
                setPage={setPage}
                setEmploymentInfo={setEmploymentInfo}
                employmentInfo={employmentInfo}
              />
            )}
            {page === OnboardPages.paymentInfo.number && (
              <PaymentInfoForm
                setPage={setPage}
                paymentInfo={paymentInfo}
                setPaymentInfo={setPaymentInfo}
              />
            )}
            {page === OnboardPages.leaveAllowance.number && (
              <LeaveAllowanceForm
                isSubmitting={isSubmitting}
                setSubmit={setSubmit}
                setPage={setPage}
                totalDaysValues={totalDaysValues}
                setTotalDaysValues={setTotalDaysValues}
              />
            )}
          </div>
        </Card>
      </div>
    </>

  );
}

export default OnboardPage;
